
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TermsAcceptance } from "./TermsAcceptance";
import { AuthNavLinks } from "./AuthNavLinks";
import { AuthError } from "./AuthError";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { SupabaseAuthForm } from "./SupabaseAuthForm";
import { useToast } from "@/hooks/use-toast";

type AuthView = 'sign_in' | 'sign_up' | 'update_password';

interface AuthFormProps {
  view: AuthView;
}

export const AuthForm = ({ view }: AuthFormProps) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string>("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleAuthStateChange = useCallback(async (event: string) => {
    console.log('Auth state changed:', event);
    if (event === 'USER_UPDATED' || event === 'SIGNED_IN') {
      try {
        const { error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          setError(sessionError.message);
          toast({
            variant: "destructive",
            title: "Authentication Error",
            description: sessionError.message,
          });
        }
      } catch (err: any) {
        setError(err.message);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to verify session. Please try again.",
        });
      }
    }
  }, [toast]);

  useEffect(() => {
    const authListener = supabase.auth.onAuthStateChange(handleAuthStateChange);
    return () => {
      authListener.data.subscription.unsubscribe();
    };
  }, [handleAuthStateChange]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Form submission attempted');
    if (view === 'sign_up' && !acceptedTerms) {
      toast({
        title: "Terms Acceptance Required",
        description: "Please accept the Terms of Service and Privacy Policy to continue.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-everest-teal"></div>
      </div>
    );
  }

  return (
    <div 
      className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" 
      onClick={(e) => e.stopPropagation()}
    >
      <AuthError message={error} />
      
      <div className="space-y-6">
        {!showForgotPassword ? (
          <>
            <SupabaseAuthForm view={view} onError={setError} />
            
            {view === 'sign_in' && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowForgotPassword(true);
                  }}
                  className="text-sm text-everest-teal hover:text-everest-teal/90 font-medium underline"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {view === 'sign_up' && (
              <form onSubmit={handleFormSubmit}>
                <TermsAcceptance 
                  acceptedTerms={acceptedTerms}
                  setAcceptedTerms={setAcceptedTerms}
                />
              </form>
            )}
          </>
        ) : (
          <ForgotPasswordForm 
            onCancel={() => setShowForgotPassword(false)}
            onLoading={setIsLoading}
          />
        )}
      </div>

      <AuthNavLinks view={view} />
    </div>
  );
};
